<script>
    import { tick } from 'svelte';
    import Icon from '../icon/Icon.svelte';

    /**
     * @typedef {Object} Props
     * @property {Array} [items] Array with objects { label, value, disabled, .. }
     * @property {string|number} [defaultValue] Initial selected value. Must by present in items
     * or null to select placeholder (default)
     * @property {string} [label]
     * @property {string} [placeholder]
     * @property {boolean} [inline]
     * @property {boolean} [disabled]
     * @property {Function} [callback] Function called when the user makes a selection, sends the selected item
     */

    /** @type {Props} */
    let {
        items = [],
        defaultValue = null,
        label = '',
        placeholder = '',
        inline = false,
        disabled = false,
        callback = () => {}
    } = $props()

    const id = _.uniqueId()

    let selectedItem = $state()

    initialize()

    function initialize() {
        if (placeholder) {
            items.unshift({
                label: placeholder,
                value: null,
                disabled: true
            })
        }

        setValue(defaultValue)
    }

    export function getValue() {
        // If selected value is not contained in the list or is the placeholder, {item: {value: null}} is returned.
        return {
            item: selectedItem
        }
    }

    export function setValue(value) {
        const matchingItem = items.find(item => item.value === value)
        if (matchingItem) {
            selectedItem = matchingItem
        }
    }

    export function setItems(newItems) {
        items = newItems
    }

    async function onChange() {
        if (callback) {
            // Allow the component to update before running the callback to prevent racing conditions
            await tick()
            callback(selectedItem)
        }
    }
</script>

<div class="quick-select" class:inline class:disabled>
    {#if label}
        <label for={id} class="label">{label}</label>
    {/if}
    <div class="select-container" component="quick-select-select-container">
        <select
            {id}
            bind:value={selectedItem}
            onchange={onChange}
            {disabled}
            title={selectedItem?.label || ''}
        >
            {#each items as item (item)}
                <option
                    value={item}
                    disabled={item.disabled}
                >{item.label}</option>
            {/each}
        </select>
        <div class="value" inert>
            {selectedItem?.label || ''}
        </div>
        <div class="arrow" inert>
            <Icon name="arrow-down"></Icon>
        </div>
    </div>
</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.quick-select {
  display: flex;
  flex-direction: column;
}
.quick-select.inline {
  display: inline-flex;
  align-items: flex-start;
}
.quick-select.disabled {
  opacity: 0.8;
}
.quick-select .label {
  font-size: 14px;
  font-variant-caps: all-small-caps;
  letter-spacing: 1px;
  line-height: 12px;
  color: hsl(205, 60%, 44%);
  margin-bottom: 8px;
}
.quick-select .select-container {
  border-radius: 16px;
  padding: 14px 16px;
  border: solid 1px hsl(40, 4%, 84%);
  background-color: #fff;
  font-size: 16px;
}
.quick-select .select-container:focus-within {
  border-color: hsl(205, 60%, 44%);
  outline: none;
}
.quick-select .select-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
}
.quick-select .select-container select {
  position: absolute;
  inset: 0;
  padding-left: 8px;
  padding-right: 8px;
  opacity: 0;
  appearance: none;
  cursor: pointer;
}
.quick-select .select-container select:disabled {
  cursor: not-allowed;
}
.quick-select .select-container .value {
  min-width: 40px;
}
.quick-select .select-container .arrow {
  display: flex;
  width: 16px;
  height: 16px;
  fill: currentColor;
}</style>
